import {ReactNode, useMemo} from 'react';

import {RootLayer} from '@sail/ui';

import {
  AppConfig,
  AppProfile,
  AppProvider,
  InlineDataProvider,
} from '@stripe-internal/conversations-assistant';
import {
  FeatureFlagsProvider as ConvUIFlagsProvider,
  type FeatureFlags as ConvUIIFlags,
  ThemeProvider as ConvUIThemeProvider,
  DefaultTheme,
  LinkTheme,
  type Theme,
} from '@stripe-internal/conversations-ui';
import {DefaultInlineDataComponentRegistry} from '@stripe-internal/mse-inline-data';
import {Observability} from '@stripe-internal/conversations-client';

const mobileConvUIFlags: ConvUIIFlags = {
  newUniversalHelpDesign: true,
  universalHelpDotComDesign: false,
  dotComFeedbackButtons: false,
  dotComNoLlm: false,
  dotComCsf: false,
  dotComCsfSelfServe: false,
  messagesTopAnchored: false,
  inlineRacEmailForm: true,
  universalHelpDrawerEnabled: false,
  conversationsLiveAnswersRow: true,
  universalHelpEncourageMoreDetailFromUserMse5042: false,
  enableScreenshareUi: false,
  enableScreenshareContextProvider: false,
  enableUhPendingInput: false,
};

type Props = {
  config: AppConfig;
  profile: AppProfile;
  observability?: Observability;
  children: ReactNode;
};

function getThemeForAudience(audience: string): Theme {
  switch (audience) {
    case 'link':
      return LinkTheme;
    case 'stripe':
    default:
      return DefaultTheme;
  }
}

export function MobileAssistantProviders({
  config,
  profile,
  observability,
  children,
}: Props) {
  const theme = useMemo(
    () => getThemeForAudience(config.audience),
    [config.audience],
  );

  return (
    <ConvUIThemeProvider value={theme}>
      <RootLayer>
        <AppProvider
          config={config}
          profile={profile}
          observability={observability}
        >
          <InlineDataProvider components={DefaultInlineDataComponentRegistry}>
            <ConvUIFlagsProvider value={mobileConvUIFlags}>
              {children}
            </ConvUIFlagsProvider>
          </InlineDataProvider>
        </AppProvider>
      </RootLayer>
    </ConvUIThemeProvider>
  );
}
